<script setup>
import { ref } from 'vue'
import { useForm, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency.js'
import UiButton from '@/Components/UI/UiButton.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import ConfirmDialog from '@/Components/UI/ConfirmDialog.vue'

const props = defineProps({ delivery: { type: Object, required: true } })
const { t } = useI18n()
const { formatMoney, formatDate } = useCurrency()
const page = usePage()
const isAdmin = ref(['admin'].includes(page.props.auth?.user?.role))
const canPay  = ref(['admin', 'staff'].includes(page.props.auth?.user?.role))
const showForm = ref(false)
const showReverseConfirm = ref(false)

const form = useForm({ method: 'cash', notes: '' })

const methodOptions = [
  { value: 'cash',          label: 'Cash' },
  { value: 'card',          label: 'Card' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'online',        label: 'Online' },
]

function submit() {
  form.transform(d => ({ delivery_payment: d }))
      .post(`/deliveries/${props.delivery.id}/payments`, {
        onSuccess: () => { showForm.value = false },
      })
}

function reverse() {
  useForm({}).delete(`/deliveries/${props.delivery.id}/payments`, {
    onSuccess: () => { showReverseConfirm.value = false },
  })
}
</script>

<template>
  <div
    class="border rounded-[var(--radius-lg)] p-4 space-y-3"
    style="background: var(--surface); border-color: var(--line);"
  >
    <h3 class="text-sm font-medium text-[color:var(--ink)]">{{ t('deliveries.show.payment.title') }}</h3>

    <dl class="grid grid-cols-2 gap-2 text-sm">
      <div>
        <dt class="text-[color:var(--muted)]">{{ t('deliveries.index.fee') }}</dt>
        <dd class="fw-value tabular-nums mt-0.5 text-[color:var(--ink)]">{{ formatMoney(delivery.fee_cents) }}</dd>
      </div>
      <div v-if="delivery.paid_at">
        <dt class="text-[color:var(--muted)]">{{ t('deliveries.show.payment.paidAt') }}</dt>
        <dd class="fw-value mt-0.5 text-[color:var(--ink)]">
          {{ formatDate(delivery.paid_at) }}
          <span v-if="delivery.paid_method" class="text-[color:var(--muted)]"> · {{ delivery.paid_method }}</span>
        </dd>
      </div>
    </dl>

    <template v-if="!delivery.paid_at && delivery.status === 'completed' && canPay">
      <UiButton
        v-if="!showForm"
        variant="primary"
        @click="showForm = true"
      >
        {{ t('deliveries.show.markPaid') }}
      </UiButton>
      <div
        v-else
        class="space-y-2"
      >
        <div>
          <p class="text-xs text-[color:var(--muted)] mb-1">{{ t('deliveries.show.method') }}</p>
          <UiSelect
            v-model="form.method"
            :options="methodOptions"
          />
        </div>
        <div>
          <p class="text-xs text-[color:var(--muted)] mb-1">{{ t('deliveries.show.notes') }}</p>
          <UiTextarea
            v-model="form.notes"
            :rows="2"
          />
        </div>
        <div class="flex gap-2">
          <UiButton
            variant="primary"
            :disabled="form.processing"
            @click="submit"
          >
            {{ t('deliveries.show.payment.confirm') }}
          </UiButton>
          <UiButton
            variant="secondary"
            @click="showForm = false"
          >
            {{ t('common.cancel') }}
          </UiButton>
        </div>
      </div>
    </template>

    <UiButton
      v-if="delivery.paid_at && isAdmin"
      variant="danger"
      @click="showReverseConfirm = true"
    >
      {{ t('deliveries.show.reverse') }}
    </UiButton>

    <ConfirmDialog
      v-if="showReverseConfirm"
      :open="showReverseConfirm"
      :title="t('deliveries.show.reverse')"
      :consequence="t('deliveries.show.reverseConfirm')"
      danger
      @confirm="reverse"
      @cancel="showReverseConfirm = false"
    />
  </div>
</template>
